import React, { useContext, useEffect, useMemo, useState } from 'react';
import MUIDataTable, { DisplayData, MUIDataTableColumnDef, MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import { Box, Button, Chip, Link, Tooltip } from '@mui/material';
import { formatNumber } from '../util/NumberUtil';
import { displayLocalDate, getCurrentYearMonth, isAfterEndOfMonth, isValidYearMonth } from '../util/TimeUtil';
import {
  createSavedFilter,
  deleteSavedFilter,
  getDashboard,
  getSavedFilters,
  resetDefaultSavedFilters,
  updateSavedFilter,
  isXpmConnected,
  setDashboardNote,
  syncDashboardEntries,
  updateDashboardEntries,
} from '../rest/BackendApi';
import { useSearchParams } from 'react-router';
import { ThemeProvider } from '@mui/material/styles';
import styles from '../style/common.module.css';
import dayjs from 'dayjs';
import TransactionsTable from '../components/TransactionsTable';
import MarkAsPendingIcon from '@mui/icons-material/PendingActions';
import MarkAsCompleteIcon from '@mui/icons-material/AssignmentTurnedIn';
import MailIcon from '@mui/icons-material/Mail';
import SyncIcon from '@mui/icons-material/Sync';
import EditNoteIcon from '@mui/icons-material/EditNoteOutlined';
import AddNoteIcon from '@mui/icons-material/NoteAddOutlined';
import { DashboardStatusEnum } from '../model/DashboardStatusEnum';
import { PeriodEnum } from '../model/PeriodEnum';
import {
  customFilterWithPrefix,
  describeFilters,
  getColumnFilters,
  matchesFilters,
  getMuiTheme,
  NoMatches,
  useTableBodyHeight,
  withFilterLists,
  withReadableFilters,
} from '../components/Datatable';
import Loading from '../components/Loading';
import NoOrganisation from '../components/NoOrganisation';
import { SectionTitle, StyledMuiLink, SubText } from '../components/StyledComponent';
import OrgSyncStatusDisplay from '../components/SyncStatus';
import { Dashboard, DashboardFilters, DashboardStatus, SavedFilter, SavedFilterDetails } from '../types/DashboardType';
import { BasisEnum } from '../model/BasisEnum';
import { humanize, plural } from '../util/StringUtil';
import UserContext from '../UserContext';
import SupportLink from '../components/SupportLink';
import { getDatatableColumnConfig, getDatatableConfig, getItemFromLocalStorage, getStoredColumnFilters, setItemInLocalStorage } from '../util/LocalStorageUtil';
import XeroLink from '../components/XeroLink';
import EmailSendingDialog, { EmailSendingDialogProps } from '../components/EmailSendingDialog';
import { Tag } from '../types/OrganisationType';
import { AddTextField } from '../components/AddTextField';
import { AutoEllipsis } from '../components/AutoEllipsis';
import { getFullName } from '../util/DataUtil';
import MessageContext, { empty, error, progress, success } from '../MessageContext';
import { hasFeature } from '../business/Subscription';
import { XpmJob } from '../types/XpmType';
import JobsTable from '../components/JobsTableDialog';
import { GenericDialog, GenericDialogProps } from '../components/GenericDialog';
import { DisplayWithSeeMoreChip } from '../components/DisplayWithSeeMoreChip';
import Checkbox from '@mui/material/Checkbox';
import { NONE_LABEL } from '../util/Constants';
import { MonthStepper, statusChipSx } from '../components/DashboardHeader';
import { filterKey, hasFilters, sameFilters, SavedFilters } from '../components/SavedFilters';
import {
  accountTransactionsState,
  bankTransactionsState,
  checkCellStyle,
  paygwState,
  payrunState,
  superState,
  wagesPayableState,
} from '../business/DashboardChecks';
import { syncResultMessage } from '../business/SyncResult';

const PAYRUN_BEFORE_LABEL = 'Payrun before period Date';
const PAYRUN_AFTER_LABEL = 'Payrun after period Date';
const NO_PAYRUN_LABEL = 'No payrun posted';
const EMPTY_CELL = '—';

export default function DashboardPage() {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);
  const [yearMonth, setYearMonth] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogContent, setDialogContent] = useState<GenericDialogProps>({ open: false });
  const [xpmConnected, setXpmConnected] = useState<boolean>(false);
  const [showHelp, setShowHelp] = useState<boolean>(getItemFromLocalStorage('showHelp') !== 'false');
  const [emailDialogContent, setEmailDialogContent] = useState<EmailSendingDialogProps>();
  // The table's filters are controlled here, so the saved filter chips can set them
  const [columnFilters, setColumnFilters] = useState<DashboardFilters>(() => getStoredColumnFilters('dashboardConfig'));
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const userContext = useContext(UserContext);
  const messageContext = useContext(MessageContext);
  const user = userContext?.user;

  const shouldShowXpm = xpmConnected;
  const shouldShowOpenAccess = hasFeature(user, 'MultiUser') && user?.firm?.openAccess;

  useEffect(() => {
    if (hasFeature(user, 'XPM')) {
      isXpmConnected(false).then(response => setXpmConnected(response.connected));
    }
  }, [user]);

  useEffect(() => {
    // Without a subscription the app is about to send them to the Subscription page, and the backend would refuse anyway
    if (!user?.hasActiveSubscription) {
      return;
    }
    getSavedFilters()
      .then(setSavedFilters)
      .catch(() => messageContext.setMessage(error("Couldn't load your saved filters")));
  }, [user?.hasActiveSubscription]);

  useEffect(() => {
    const monthValue = searchParams.get('month');
    if (isValidYearMonth(monthValue)) {
      setYearMonth(monthValue);
    } else {
      const storedYearMonth = getItemFromLocalStorage('dashboardYearMonth');
      if (storedYearMonth && isValidYearMonth(storedYearMonth)) {
        setYearMonth(storedYearMonth);
      } else {
        setYearMonth(getCurrentYearMonth());
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (yearMonth && isValidYearMonth(yearMonth)) {
      // Update the URL before loading: changing it re-renders the page, which is slow once hundreds of rows are shown
      setSearchParams({ month: yearMonth });
      fetchData().then(() => setItemInLocalStorage('dashboardYearMonth', yearMonth));
    }
  }, [yearMonth]);

  const updateShowHelp = (state: boolean) => {
    setShowHelp(state);
    setItemInLocalStorage('showHelp', state ? 'true' : 'false');
  };

  async function saveFilter(details: SavedFilterDetails) {
    try {
      const savedFilter = await createSavedFilter(details, columnFilters);
      setSavedFilters(current => [...current, savedFilter]);
      messageContext.setMessage(success(`Saved filter "${details.name}"`, { icon: 'save' }));
    } catch {
      messageContext.setMessage(error("Couldn't save the filter. Please try again later."));
    }
  }

  async function updateFilter(savedFilter: SavedFilter, details: SavedFilterDetails) {
    try {
      const updated = await updateSavedFilter(savedFilter, details);
      setSavedFilters(current => current.map(f => (filterKey(f) === filterKey(savedFilter) ? updated : f)));
      messageContext.setMessage(success(`Saved filter "${details.name}"`, { icon: 'save' }));
    } catch {
      messageContext.setMessage(error("Couldn't save the filter. Please try again later."));
    }
  }

  async function removeSavedFilter(savedFilter: SavedFilter) {
    try {
      await deleteSavedFilter(savedFilter);
      const isRemoved = (f: SavedFilter) => filterKey(f) === filterKey(savedFilter);
      // A removed default stays in the list as hidden, so the Filters dialog knows Reset would bring it back
      setSavedFilters(current =>
        savedFilter.defaultStatus ? current.map(f => (isRemoved(f) ? { ...f, hidden: true } : f)) : current.filter(f => !isRemoved(f)),
      );
      if (sameFilters(savedFilter.filters, columnFilters)) setColumnFilters({});
      messageContext.setMessage(success(`Removed filter "${savedFilter.name}"`));
    } catch {
      messageContext.setMessage(error("Couldn't remove the filter. Please try again later."));
    }
  }

  async function resetDefaults() {
    try {
      setSavedFilters(await resetDefaultSavedFilters());
      messageContext.setMessage(success('Reset the default filters'));
    } catch {
      messageContext.setMessage(error("Couldn't reset the default filters. Please try again later."));
    }
  }

  const fetchData = async (afterSync?: boolean) => {
    try {
      setLoadingMessage(afterSync ? 'Initialising dashboard, this may take a few minutes' : 'Loading dashboard');
      setLoading(true);
      await refreshDataBackground();
    } finally {
      setLoading(false);
      setLoadingMessage(undefined);
    }
  };

  const refreshDataBackground = async () => {
    const dashboards = await getDashboard(yearMonth);
    if (dashboards) {
      setDashboards(dashboards);
    }
  };

  const onSyncReady = (syncStatus: any, attempt: number) => {
    if (syncStatus == null) {
      return;
    }
    // If attempt was greater than 1 or attempt 1 completed less than few seconds ago we refresh
    let syncCompletionTimeInMs = dayjs().diff(dayjs.utc(syncStatus.updateDate));
    if (attempt > 1 || syncCompletionTimeInMs < 5000) {
      fetchData(true);
    }
  };

  function replaceEntries(updatedEntries: Dashboard[]) {
    const mappedEntry = new Map<number, Dashboard>(updatedEntries.map(element => [element.id, element]));
    setDashboards(current => current.map(entry => mappedEntry.get(entry.id) || entry));
  }

  async function markEntriesAs(selected: Dashboard[], setSelected: Function, status: DashboardStatusEnum) {
    // Show progress straight away, like Sync with Xero, so it's clear something is happening while the API call runs
    messageContext.setMessage(progress(`Marking ${plural(selected.length, 'client')} as ${status.toLowerCase()}`));
    setSelected([]);
    try {
      replaceEntries(
        await updateDashboardEntries(
          selected.map(row => row.id),
          status,
        ),
      );
      messageContext.setMessage(
        success(`Marked ${plural(selected.length, 'client')} as ${status.toLowerCase()}`, {
          icon: status === DashboardStatusEnum.COMPLETE ? 'complete' : 'pending',
          action: { label: 'Undo', onClick: () => restoreStatuses(selected) },
        }),
      );
    } catch (exception) {
      messageContext.setMessage(
        error(
          <span>
            Couldn't mark as "{humanize(status)}". Please try again later or <SupportLink />
          </span>,
        ),
      );
    }
  }

  // Undo: put each entry back to the status it had before, e.g. "Ready to start" rather than always "Pending".
  async function restoreStatuses(previous: Dashboard[]) {
    messageContext.setMessage(progress(`Restoring ${plural(previous.length, 'client')} to the previous status`));
    try {
      const statuses = [...new Set(previous.map(entry => entry.status))];
      for (const status of statuses) {
        const ids = previous.filter(entry => entry.status === status).map(entry => entry.id);
        replaceEntries(await updateDashboardEntries(ids, status));
      }
      messageContext.setMessage(success(`Restored ${plural(previous.length, 'client')} to the previous status`, { icon: 'undo' }));
    } catch (exception) {
      messageContext.setMessage(error("Couldn't undo the change. Please set the status again."));
    }
  }

  async function markAsComplete(selected: Dashboard[], setSelected: Function) {
    return markEntriesAs(selected, setSelected, DashboardStatusEnum.COMPLETE);
  }

  async function markAsPending(selected: Dashboard[], setSelected: Function) {
    return markEntriesAs(selected, setSelected, DashboardStatusEnum.PENDING);
  }

  async function syncWithXero(selected: Dashboard[], setSelected: Function) {
    // Sync in the background with a progress banner, so the dashboard stays usable
    messageContext.setMessage(progress(`Syncing ${plural(selected.length, 'organisation')} with Xero`));
    setSelected([]);
    try {
      const updatedEntries = await syncDashboardEntries(selected.map(row => row.id));
      const mappedEntry = new Map<number, Dashboard>(updatedEntries.map(element => [element.id, element]));
      setDashboards(current => current.map(entry => mappedEntry.get(entry.id) || entry));
      messageContext.setMessage(
        syncResultMessage(
          selected.map(row => ({ id: row.id, name: row.organisation.name })),
          updatedEntries.map(entry => entry.id),
        ),
      );
    } catch (exception) {
      messageContext.setMessage(error('Failed to sync with Xero. Log out and log back in if the problem persists.'));
    }
  }

  function updateDashboardEntryAfterPostApiCall(updatedDashboard: Dashboard) {
    setDashboards(current => current.map(d => (d.id === updatedDashboard.id ? updatedDashboard : d)));
  }

  function getStatusOrder(status: string): number {
    switch (status) {
      case 'PENDING':
        return 2;
      case 'READY_TO_START':
        return 1;
      case 'COMPLETE':
        return 3;
      case 'NOT_REPORTING':
        return 4;
    }
    return 0;
  }

  function showDetailUnreconciledTransactions(kind: 'bank' | 'account', entry: Dashboard, event: any) {
    const transactions = kind === 'bank' ? entry.unreconciledBankTransactions : entry.unreconciledAccountTransactions;
    const total = kind === 'bank' ? entry.nbUnreconciledBankTransactions : entry.nbUnreconciledAccountTransactions;
    setDialogContent({
      content: <TransactionsTable transactions={transactions} total={total} shortCode={entry.organisation.shortCode}></TransactionsTable>,
      title: plural(total, `unreconciled ${kind} transaction`),
      subtitle: entry.organisation.name + ' · ' + (kind === 'bank' ? 'Bank transactions from the last 6 months' : 'Payments not matched to a bank transaction'),
      open: true,
      onClose: () => setDialogContent({ open: false }),
    });
    event.preventDefault();
    event.stopPropagation();
  }

  const numberBodyRender = (value: any) => {
    return formatNumber(value);
  };

  const zeroOrOtherFilterOptions = {
    names: ['$0.00', 'Other'],
    logic: (location: any, filters: any, row: any) => {
      let value = 'Other';
      if (location == '$0.00') {
        value = '$0.00';
      }
      if (filters.length) return !filters.includes(value);
      return false;
    },
  };

  const transactionsFilterOptions = {
    names: ['0', '1-5', '6-10', '10+'],
    logic: (body: any, filters: any, row: any) => {
      let value = '0';
      if (body > 10) {
        value = '10+';
      } else if (body > 5) {
        value = '6-10';
      } else if (body > 0) {
        value = '1-5';
      }
      if (filters.length) return !filters.includes(value);
      return false;
    },
  };

  // Column names and filter labels are saved in users' saved filters (server side): renaming them breaks those filters.
  const readableColumns: MUIDataTableColumnDef[] = withReadableFilters(
    [
      {
        name: 'xpmJobs',
        options: {
          display: 'excluded',
          filter: false,
          download: false,
        },
      },
      {
        name: 'organisation.name',
        label: 'Name',
        options: {
          filter: false,
          sort: true,
          customBodyRenderLite: (dataIndex: number) => {
            const row = dashboards[dataIndex];
            return (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: { md: '250px' } }}>
                  <div>
                    <AutoEllipsis text={row.organisation.name} maxLength={39} /> <XeroLink shortCode={row.organisation.shortCode} />
                  </div>
                </Box>
                <SubText>
                  Synced {dayjs.utc(row.lastSynced).fromNow()}
                  {row.lastReminderSent && ' - Last email ' + dayjs.utc(row.lastReminderSent).fromNow()}
                </SubText>
              </>
            );
          },
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          hint:
            showHelp &&
            'Status:\n\n' +
              'Automatically moves to "ready to start" when the period is finished and no unreconciled bank transaction \n\n' +
              'Can be changed to complete by selecting the checkbox for that row and clicking on the mark as complete icon',
          filter: true,
          filterType: 'multiselect',
          filterOptions: {
            names: Object.values(DashboardStatusEnum),
            logic(status: string, filterVal: string[]) {
              return !filterVal.includes(DashboardStatusEnum[status as keyof typeof DashboardStatusEnum]);
            },
          },
          sort: true,
          sortCompare: (order: string) => {
            return (obj1: any, obj2: any) => {
              let val1 = getStatusOrder(obj1.data);
              let val2 = getStatusOrder(obj2.data);
              return (val1 - val2) * (order === 'asc' ? 1 : -1);
            };
          },
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: DashboardStatus) => {
            const label = DashboardStatusEnum[value as keyof typeof DashboardStatusEnum];
            // Filled = needs action (ready to start) or done; outlined = waiting/not applicable.
            const filled = value === 'READY_TO_START' || value === 'COMPLETE';
            return <Chip size="small" label={label} variant={filled ? 'filled' : 'outlined'} sx={{ whiteSpace: 'nowrap', ...statusChipSx(value, filled) }} />;
          },
        },
      },
      shouldShowOpenAccess && {
        name: 'organisation.connectedToCurrentUser',
        label: 'Organisation connected by',
        options: {
          filter: true,
          download: false,
          ...customFilterWithPrefix('Connected by'),
          display: 'excluded',
          customBodyRender: (data: boolean) => {
            return data ? 'You' : 'Firm';
          },
        },
      },
      {
        name: 'organisation.manager',
        label: 'Client Manager',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'organisation.manager')?.display || 'false',
          filter: true,
          download: false,
          filterType: 'multiselect',
          filterOptions: {
            names: [...new Set(dashboards.map(dash => (dash.organisation.manager ? getFullName(dash.organisation.manager) : 'None')))].sort(),
            logic: (body: any, filters: any, row: any) => {
              if (body == null) {
                return !filters.includes('None');
              }
              if (filters.length) return !filters.includes(body);
              return false;
            },
          },
          ...customFilterWithPrefix('Manager'),
          sort: true,
          customBodyRender: (manager: PublicUser | null) => {
            return manager && getFullName(manager);
          },
        },
      },
      {
        name: 'organisation.tags',
        label: 'Organisation Tags',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'organisation.tags')?.display || 'false',
          sort: false,
          filter: true,
          download: false,
          filterType: 'multiselect',
          filterOptions: {
            names: [NONE_LABEL, ...new Set(dashboards.flatMap(dash => dash.organisation.tags.map(t => t.name)))],
            logic: (tags: Tag[], filters: string[]) => {
              const tagNames = tags.map(t => t.name);
              if (tagNames.length == 0) {
                return !filters.includes(NONE_LABEL);
              }
              return !filters.some((filter: string) => tagNames.includes(filter));
            },
          },
          ...customFilterWithPrefix('Tag'),
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRenderLite(dataIndex: number) {
            const tags = dashboards[dataIndex].organisation.tags;
            return (
              <div className={styles.centered} style={{ gap: 2 }}>
                {tags.map(t => (
                  <Chip key={t.id} variant="outlined" size="small" label={t.name} />
                ))}
              </div>
            );
          },
        },
      },
      {
        name: 'organisation.basPeriod',
        label: 'Reporting',
        options: {
          filter: true,
          filterOptions: {
            names: [PeriodEnum.MONTHLY, PeriodEnum.QUARTERLY],
            logic(period: string, filterVal: string[]) {
              return !filterVal.includes(PeriodEnum[period as keyof typeof PeriodEnum]);
            },
          },
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRenderLite: (dataIndex: number) => {
            const org = dashboards[dataIndex].organisation;
            return (
              <Tooltip
                title={
                  <span>
                    GST: {PeriodEnum[org.gstPeriod as keyof typeof PeriodEnum]}
                    <br /> PAYGW: {PeriodEnum[org.paygwPeriod as keyof typeof PeriodEnum]}
                  </span>
                }
              >
                <span>{PeriodEnum[org.basPeriod as keyof typeof PeriodEnum]}</span>
              </Tooltip>
            );
          },
        },
      },
      {
        name: 'organisation.gstBasis',
        label: 'GST Basis',
        options: {
          filterOptions: {
            names: [BasisEnum.ACCRUAL, BasisEnum.CASH],
            logic(basis: string, filterVal: string[]) {
              return !filterVal.includes(basis);
            },
          },
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: any) => {
            return BasisEnum[value as keyof typeof BasisEnum];
          },
        },
      },
      {
        name: 'organisation.periodLockDate',
        label: 'User Lock Date',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'organisation.periodLockDate')?.display || 'false',
          filter: false,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: string) => {
            return value ? displayLocalDate(value) : EMPTY_CELL;
          },
        },
      },
      {
        name: 'organisation.endOfYearLockDate',
        label: 'Adviser Lock Date',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'organisation.endOfYearLockDate')?.display || 'false',
          filter: false,
          sort: true,
          setCellProps: () => {
            return { align: 'center' };
          },
          customBodyRender: (value: string) => {
            return value ? displayLocalDate(value) : EMPTY_CELL;
          },
        },
      },
      {
        name: 'nbUnreconciledBankTransactions',
        label: 'Unreconciled Bank Transactions',
        options: {
          hint: showHelp && 'Unreconciled Bank Transactions from the last 6 months',
          filter: true,
          sort: true,
          ...customFilterWithPrefix('Unreconciled Bank Transactions'),
          filterOptions: {
            ...transactionsFilterOptions,
          },
          setCellHeaderProps: () => {
            return { style: { maxWidth: '145px' } };
          },
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'center', style: checkCellStyle(bankTransactionsState(dashboards[dataIndex])) };
          },
          customBodyRenderLite: (dataIndex: number) => {
            let entry = dashboards[dataIndex];
            const value = entry.nbUnreconciledBankTransactions;
            return value > 0 ? (
              <Link
                component="button"
                aria-label={`Show ${value} unreconciled bank transactions`}
                onClick={e => showDetailUnreconciledTransactions('bank', entry, e)}
              >
                {value}
              </Link>
            ) : (
              0
            );
          },
        },
      },
      {
        name: 'nbUnreconciledAccountTransactions',
        label: 'Unreconciled Account Transactions',
        options: {
          hint: showHelp && 'Spend/Receive money and Accounts Receivable\nPayable payments not matched to a bank transaction',
          filter: true,
          sort: true,
          ...customFilterWithPrefix('Unreconciled Account Transactions'),
          filterOptions: {
            ...transactionsFilterOptions,
          },
          setCellHeaderProps: () => {
            return { style: { maxWidth: '145px' } };
          },
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'center', style: checkCellStyle(accountTransactionsState(dashboards[dataIndex])) };
          },
          customBodyRenderLite: (dataIndex: number) => {
            let entry = dashboards[dataIndex];
            const value = entry.nbUnreconciledAccountTransactions;
            return value > 0 ? (
              <Link
                component="button"
                aria-label={`Show ${value} unreconciled account transactions`}
                onClick={e => showDetailUnreconciledTransactions('account', entry, e)}
              >
                {value}
              </Link>
            ) : (
              0
            );
          },
        },
      },
      {
        name: 'paygwBas',
        label: 'PAYGW BAS',
        options: {
          hint: showHelp && 'PAYG Withholding accrued for the period retrieved from payruns',
          ...customFilterWithPrefix('PAYGW BAS'),
          filterOptions: {
            ...zeroOrOtherFilterOptions,
          },
          customBodyRender: numberBodyRender,
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'right', style: { ...checkCellStyle(paygwState(dashboards[dataIndex])), paddingRight: '10px' } };
          },
        },
      },
      {
        name: 'paygwAccount',
        label: 'PAYGW Account',
        options: {
          hint: showHelp && 'Balance of the PAYG Withholding account from the Balance Sheet\n(Cash/Accrual) as of period end date',
          ...customFilterWithPrefix('PAYGW Account'),
          filterOptions: {
            ...zeroOrOtherFilterOptions,
          },
          customBodyRender: numberBodyRender,
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'right', style: { ...checkCellStyle(paygwState(dashboards[dataIndex])), paddingRight: '10px' } };
          },
        },
      },
      {
        name: 'wagesPayable',
        label: 'Wages Payable',
        options: {
          hint: showHelp && 'Balance of the Wages Payable account from the Balance Sheet\n(Cash/Accrual) as of period end date',
          ...customFilterWithPrefix('Wages Payable'),
          filterOptions: {
            ...zeroOrOtherFilterOptions,
          },
          customBodyRender: numberBodyRender,
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'right', style: { ...checkCellStyle(wagesPayableState(dashboards[dataIndex])), paddingRight: '10px' } };
          },
        },
      },
      {
        name: 'super',
        label: 'Super Payable/Accrued',
        options: {
          hint:
            showHelp &&
            'Balance of the Super Payable account from the Balance Sheet\n(Cash/Accrual) versus what has been accrued in payruns for the quarter to date',
          sort: false,
          filter: false,
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'center', style: checkCellStyle(superState(dashboards[dataIndex])) };
          },
          customBodyRenderLite: (dataIndex: number) => {
            const row = dashboards[dataIndex];
            return formatNumber(row.superPayable) + ' / ' + formatNumber(row.superAccrued);
          },
        },
      },
      {
        name: 'lastPayrunPosted',
        label: 'Last Payrun Posted',
        options: {
          filterOptions: {
            names: [PAYRUN_BEFORE_LABEL, PAYRUN_AFTER_LABEL, NO_PAYRUN_LABEL],
            logic(status: string, filterVal: string[]) {
              if (status == null || status == '') {
                return !filterVal.includes(NO_PAYRUN_LABEL);
              }

              const value = isAfterEndOfMonth(yearMonth, status) ? PAYRUN_AFTER_LABEL : PAYRUN_BEFORE_LABEL;
              return !filterVal.includes(value);
            },
          },
          setCellProps: (value: any, dataIndex: number) => {
            return { align: 'center', style: checkCellStyle(payrunState(yearMonth, dashboards[dataIndex])) };
          },
          customBodyRender: (value: string) => {
            return value ? displayLocalDate(value) : EMPTY_CELL;
          },
        },
      },
      {
        name: 'gstBalance',
        label: 'GST Balance',
        options: {
          hint: showHelp && 'Balance of the GST account from the Balance Sheet (Cash/Accrual) as of period end date',
          ...customFilterWithPrefix('GST Balance'),
          filterOptions: {
            ...zeroOrOtherFilterOptions,
          },
          customBodyRender: numberBodyRender,
          setCellProps: () => {
            return { align: 'right', style: { paddingRight: '10px' } };
          },
        },
      },
      {
        name: 'note',
        label: 'Note',
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex: number) => {
            const row = dashboards[dataIndex];
            return (
              <div style={{ width: '100%' }}>
                <AddTextField
                  hoverText={row.note}
                  allowEmpty={true}
                  addIcon={row.note ? <EditNoteIcon sx={{ color: '#FFD100' }} /> : <AddNoteIcon color="action" />}
                  rows={4}
                  editMinWidth="300px"
                  textEditLabel="Note"
                  initialValue={row.note || ''}
                  onSubmit={async value => {
                    try {
                      const updatedDashboard = await setDashboardNote(row.id, value);
                      updateDashboardEntryAfterPostApiCall(updatedDashboard);
                    } catch (e) {
                      messageContext.setMessage(error('Failed to save the note. Please try again.'));
                    }
                  }}
                />
              </div>
            );
          },
        },
      },
      shouldShowXpm && {
        name: 'xpmJobs.list',
        label: 'Xpm Job',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'xpmJobs.list')?.display || false,
          download: false,
          filter: false,
          sort: false,
          customBodyRender: (_: XpmJob[], tableMeta: MUIDataTableMeta) => {
            const jobs = tableMeta.rowData[0];
            if (!jobs || jobs.length == 0) {
              return '';
            } else if (jobs && jobs.length == 1) {
              return (
                <Link rel="noopener noreferrer" target="_blank" href={jobs[0].webUrl}>
                  <AutoEllipsis text={jobs[0].name} maxLength={18} />
                </Link>
              );
            } else {
              return (
                <StyledMuiLink
                  onClick={event => {
                    setDialogContent({
                      content: <JobsTable jobs={jobs}></JobsTable>,
                      title: 'Xpm Jobs',
                      open: true,
                      onClose: () => setDialogContent({ open: false }),
                    });
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                >
                  Multiple jobs found
                </StyledMuiLink>
              );
            }
          },
        },
      },
      shouldShowXpm && {
        name: 'xpmJobs.staff',
        label: 'Xpm Staff',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'xpmJobs.staff')?.display || false,
          download: false,
          filter: true,
          filterType: 'multiselect',
          filterOptions: {
            names: [...new Set(dashboards.flatMap(dash => dash.xpmJobs?.[0]?.staff).map(staff => (staff ? getFullName(staff) : 'None')))].sort(),
            logic: (fullName: string, filters: string[], row: any) => {
              const jobs = row[0];
              const staff: PublicUser[] | undefined = jobs?.[0]?.staff;
              const staffFullNames = staff?.map(s => getFullName(s)) || [];
              if (!staffFullNames || staffFullNames.length == 0) {
                return !filters.includes('None');
              }
              if (filters.length) return !filters.some((filter: string) => staffFullNames.includes(filter));
              return false;
            },
          },
          ...customFilterWithPrefix('Xpm Staff'),
          sort: true,
          customBodyRender(_: any, tableMeta: MUIDataTableMeta) {
            const jobs = tableMeta.rowData[0];
            const staff: PublicUser[] | undefined = jobs?.[0]?.staff;
            const staffFullNames = staff?.map(s => getFullName(s)) || [];
            return (
              <>
                <DisplayWithSeeMoreChip nbRowsDisplayed={1} separator={'\n'} rows={staffFullNames} />
              </>
            );
          },
        },
      },
      shouldShowXpm && {
        name: 'xpmJobs.manager',
        label: 'Xpm Manager',
        options: {
          display: getDatatableColumnConfig('dashboardConfig', 'xpmJobs.manager')?.display || false,
          download: false,
          filter: true,
          filterType: 'multiselect',
          filterOptions: {
            names: [...new Set(dashboards.map(dash => (dash.xpmJobs?.[0]?.manager ? getFullName(dash.xpmJobs[0].manager) : 'None')))].sort(),
            logic: (fullName: string, filters: any) => {
              if (!fullName) {
                return !filters.includes('None');
              }
              if (filters.length) return !filters.includes(fullName);
              return false;
            },
          },
          ...customFilterWithPrefix('Xpm Manager'),
          sort: true,
          customBodyRender(value: any, tableMeta: MUIDataTableMeta) {
            const jobs = tableMeta.rowData[0];
            const manager = jobs?.[0]?.manager;
            return manager ? getFullName(manager) : '';
          },
        },
      },
    ].filter(Boolean) as MUIDataTableColumnDef[],
  );
  const columns = withFilterLists(readableColumns, columnFilters);
  // How many rows each saved filter matches. Columns change with the data, the month (payrun logic) and XPM.
  const savedFilterCounts = useMemo(
    () => Object.fromEntries(savedFilters.map(f => [filterKey(f), dashboards.filter(row => matchesFilters(columns, row, f.filters)).length])),
    [dashboards, savedFilters, yearMonth, xpmConnected],
  );

  const getHeaderSize = () => {
    // Success messages show as a toast over the page, so only errors and progress banners take up room.
    if (messageContext.message?.content && messageContext.message.type !== 'success') {
      return '248px';
    } else {
      return '220px';
    }
  };

  const tableBodyHeight = useTableBodyHeight(`calc(100vh - ${getHeaderSize()})`);

  const options: MUIDataTableOptions = {
    filterType: 'dropdown',
    enableNestedDataAccess: '.',
    rowsPerPageOptions: [25, 50, 100],
    rowsPerPage: getDatatableConfig('dashboardConfig', 'rowsPerPage') || 50,
    print: false,
    storageKey: 'dashboardConfig',
    tableBodyHeight,
    textLabels: {
      body: {
        toolTip: '',
        noMatch: (
          <NoMatches message="No clients match" onClear={hasFilters(columnFilters) ? () => setColumnFilters({}) : undefined} clearLabel="Clear filters" />
        ),
      },
    },
    fixedSelectColumn: true,
    onFilterChange: (_, filterList) => setColumnFilters(getColumnFilters(columns, filterList)),
    fixedHeader: true,
    searchPlaceholder: 'Search clients',
    downloadOptions: {
      filename: `bas-ready-${yearMonth}.csv`,
    },
    setTableProps: () => {
      return {
        // material ui v5 only
        size: 'small',
        //border :'1'
      };
    },
    customToolbarSelect: function (
      selectedRows: { data: Array<{ index: number; dataIndex: number }>; lookup: { [key: number]: boolean } },
      _: DisplayData,
      setSelectedRows: (rows: number[]) => void,
    ) {
      if (selectedRows.data.length > 0) {
        const selectedData: Dashboard[] = selectedRows.data.map(row => dashboards[row.dataIndex]);

        const anyComplete = selectedData.some(entry => entry.status === 'COMPLETE');
        const allComplete = selectedData.every(entry => entry.status === 'COMPLETE');

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2, flexWrap: 'wrap' }}>
            {!allComplete && (
              <Button size="small" variant="contained" startIcon={<MarkAsCompleteIcon />} onClick={() => markAsComplete(selectedData, setSelectedRows)}>
                Mark complete
              </Button>
            )}
            {anyComplete && (
              <Button size="small" variant="outlined" startIcon={<MarkAsPendingIcon />} onClick={() => markAsPending(selectedData, setSelectedRows)}>
                Mark pending
              </Button>
            )}
            {hasFeature(user, 'Emails') && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MailIcon />}
                onClick={() => {
                  messageContext.setMessage(empty());
                  setEmailDialogContent({
                    open: true,
                    dashboardIds: selectedData.map(row => row.id),
                    onClose: (result: any) => {
                      const showResult = (result: any) => {
                        if (result?.error) {
                          messageContext.setMessage(error(result.error));
                        } else if (result?.success) {
                          refreshDataBackground();
                          messageContext.setMessage(success(result.success, { icon: 'email' }));
                        }
                      };
                      if (result?.sending) {
                        messageContext.setMessage(progress(result.sending));
                        result.done.then(showResult);
                      } else {
                        showResult(result);
                      }
                      setEmailDialogContent(undefined);
                      setSelectedRows([]);
                    },
                  });
                }}
              >
                Send reminder
              </Button>
            )}
            <Button size="small" variant="outlined" startIcon={<SyncIcon />} onClick={() => syncWithXero(selectedData, setSelectedRows)}>
              Sync with Xero
            </Button>
          </Box>
        );
      }
    },
  };

  return (
    <ThemeProvider theme={getMuiTheme()}>
      <GenericDialog {...dialogContent} />

      <EmailSendingDialog
        dashboardIds={emailDialogContent?.dashboardIds}
        open={emailDialogContent?.open || false}
        onClose={emailDialogContent?.onClose || (() => {})}
      />

      <OrgSyncStatusDisplay syncReadyCallback={onSyncReady} />

      {loading ? (
        <Loading message={loadingMessage} />
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 3, rowGap: 1, px: 1, pt: 1, pb: 0.5 }}>
            <SectionTitle style={{ marginBottom: 0 }}>BAS Dashboard</SectionTitle>
            <MonthStepper
              yearMonth={yearMonth || getCurrentYearMonth()}
              onChange={newYearMonth => isValidYearMonth(newYearMonth) && setYearMonth(newYearMonth)}
            />
            <SavedFilters
              savedFilters={savedFilters}
              currentFilters={columnFilters}
              onApply={setColumnFilters}
              onSave={saveFilter}
              onUpdate={updateFilter}
              onRemove={removeSavedFilter}
              counts={savedFilterCounts}
              onResetDefaults={resetDefaults}
              describeFilters={filters => describeFilters(columns, filters)}
            />
          </Box>

          {dashboards.length === 0 ? (
            <NoOrganisation />
          ) : (
            <>
              <MUIDataTable title="" data={dashboards || []} columns={columns} options={options} />
              <div style={{ fontStyle: 'italic', color: 'gray' }} className={styles.right}>
                Show help
                <Checkbox
                  size="small"
                  onChange={e => updateShowHelp(e.target.checked)}
                  checked={showHelp}
                  color="default"
                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                />
              </div>
            </>
          )}
        </>
      )}
    </ThemeProvider>
  );
}
